import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  ArrowRight, 
  Shield, 
  Clock, 
  Users, 
  CheckCircle, 
  Star,
  Leaf,
  Snowflake,
  Building2,
  Home as HomeIcon,
  MapPin,
  Truck,
  TreePine,
  Shovel,
  Hammer
} from 'lucide-react';
import { SpringPromoBanner, SpringPromoSection } from '@/components/SpringPromo';
import { GoogleReviews } from '@/components/GoogleReviews';
import { SEO, pageSEO } from '@/components/SEO';
import { SEASON_MODE, getCurrentSeasonConfig, cityPages, blogPostsForLinking } from '@/config/seasonConfig';
import { handleImageError } from '@/components/OptimizedImage';

// Icon mapping for dynamic services
const serviceIcons = {
  'lawn-maintenance': Leaf,
  'landscaping-design': TreePine,
  'hardscapes': Hammer,
  'tree-service': TreePine,
  'mulching': Shovel,
  'power-sweeping': Truck,
  'residential': HomeIcon,
  'commercial': Building2,
  'snow': Snowflake,
};

export default function Home() {
  const seasonCfg = getCurrentSeasonConfig();
  
  // Override SEO based on season
  const seo = SEASON_MODE === 'landscaping' 
    ? {
        ...pageSEO.home,
        title: seasonCfg.homeTitle,
        description: seasonCfg.homeDescription,
      }
    : pageSEO.home;
  
  // Get services from season config
  const services = seasonCfg.primaryServices.slice(0, 4).map(service => ({
    ...service,
    icon: serviceIcons[service.id] || Leaf,
  }));

  const trustFactors = [
    { icon: Shield, label: 'SBE Certified', desc: 'Small Business Enterprise certified' },
    { icon: Clock, label: 'Since 2014', desc: '10+ years serving Metro Milwaukee' },
    { icon: Users, label: 'Local Team', desc: 'Consistent crews who know your property' },
    { icon: CheckCircle, label: 'Satisfaction Guaranteed', desc: "We don't stop until you're satisfied" },
  ];

  const testimonials = [
    {
      quote: "WayBetter has been taking care of our lawn for three seasons now. They're always on time, the crews are professional, and our yard has never looked better.",
      author: "Homeowner",
      location: "Brookfield, WI",
      rating: 5,
    },
    {
      quote: "We switched to WayBetter for snow removal and it was the best decision. They're out here before our employees arrive, every single time.",
      author: "Property Manager",
      location: "Menomonee Falls, WI",
      rating: 5,
    },
    {
      quote: "The patio they installed exceeded our expectations. Great communication throughout the project and the finished product is beautiful.",
      author: "Homeowner",
      location: "New Berlin, WI",
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO {...seo} />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center pt-40 md:pt-48" 
        data-testid="hero-section"
      >
        {/* Background Image - LCP optimized */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/home-hero.webp"
            alt="Professional landscaping crew with equipment"
            className="w-full h-full object-cover"
            width="700"
            height="378"
            fetchPriority="high"
            loading="eager"
            decoding="sync"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <p className="font-barlow uppercase tracking-widest text-accent text-sm font-bold mb-4 animate-fade-in-up">
              {seasonCfg.heroTagline}
            </p>
            <h1 className="font-barlow font-bold text-5xl md:text-6xl lg:text-7xl text-white uppercase leading-[0.95] mb-6 animate-fade-in-up delay-100">
              {seasonCfg.homeH1}
            </h1>
            <p className="font-manrope text-xl text-stone-200 mb-4 animate-fade-in-up delay-200">
              {seasonCfg.heroCopy}
            </p>
            
            {/* Summer Offer Highlight */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 mb-6 animate-fade-in-up delay-250">
              <p className="font-barlow font-bold text-accent uppercase tracking-wider text-sm mb-1">
                Limited Time Summer Offer
              </p>
              <p className="font-manrope text-white text-lg">
                Get $75 off your first package with a free, no-obligation property assessment — limited availability.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-accent text-stone-900 hover:bg-accent-400 shadow-truck active:translate-y-[2px] active:shadow-none transition-all font-barlow font-bold uppercase tracking-wider py-4 px-8 text-lg"
                data-testid="hero-quote-btn"
              >
                Claim Your Free Assessment
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="tel:414-795-3610"
                className="inline-flex items-center justify-center gap-2 bg-white text-primary border-2 border-white hover:bg-stone-100 shadow-truck-primary active:translate-y-[2px] active:shadow-none transition-all font-barlow font-bold uppercase tracking-wider py-4 px-8 text-lg"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                (414) 795-3610
              </a>
            </div>
          </div>
        </div>

        {/* Trust Bar */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="bg-white/95 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex flex-wrap justify-center md:justify-between items-center gap-4 md:gap-8">
                {trustFactors.map((factor, index) => (
                  <div key={index} className="flex items-center gap-3 text-stone-700">
                    <factor.icon className="w-6 h-6 text-primary" />
                    <span className="font-barlow font-semibold uppercase tracking-wide text-sm">{factor.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 md:py-32 bg-stone-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-barlow uppercase tracking-widest text-primary text-sm font-bold mb-4">What We Do</p>
            <h2 className="font-barlow font-bold text-4xl md:text-5xl text-stone-900 uppercase mb-4">
              Professional Lawn Care & Landscaping Services
            </h2>
            <div className="section-divider mx-auto"></div>
            <p className="font-manrope text-stone-600 max-w-2xl mx-auto mt-6 text-lg">
              From weekly residential lawn maintenance to full commercial snow removal programs, we keep your Milwaukee and Menomonee Falls property looking sharp all year long. 
              <span className="text-primary font-semibold"> Get $75 off your first package with a free, no-obligation property assessment — limited availability.</span>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="service-card group h-[400px] rounded-lg"
                data-testid={`service-card-${index}`}
              >
                <img 
                  src={service.image} 
                  alt={service.title}
                  loading="lazy"
                  decoding="async"
                  width="900"
                  height="600"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="service-card-content flex flex-col justify-end h-full p-8 text-white">
                  <service.icon className="w-10 h-10 mb-4 text-accent" />
                  <h3 className="font-barlow font-bold text-2xl uppercase mb-2">{service.title}</h3>
                  <p className="font-manrope text-stone-200 mb-4">{service.description}</p>
                  <span className="inline-flex items-center gap-2 text-accent font-barlow font-semibold uppercase tracking-wider text-sm group-hover:gap-4 transition-all">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Snow Contract Season Banner */}
      <section className="bg-slate-800 py-6 md:py-8" data-testid="snow-season-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
            <div className="text-center md:text-left">
              <h2 className="font-barlow font-bold text-xl md:text-2xl uppercase text-white mb-1">
                Booking 2026-27 Snow Contracts Now
              </h2>
              <p className="font-manrope text-slate-300 text-sm md:text-base">
                Residential driveways, commercial lots and sidewalks. Lock in your spot before the season fills up.
              </p>
            </div>
            <Link
              to="/snow-removal"
              className="inline-flex items-center justify-center gap-2 bg-accent text-stone-900 hover:bg-accent-400 shadow-truck active:translate-y-[2px] active:shadow-none transition-all font-barlow font-bold uppercase tracking-wider py-3 px-6 text-sm md:text-base whitespace-nowrap flex-shrink-0"
              data-testid="snow-banner-btn"
            >
              Get a Snow Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Spring Promo Section */}
      <SpringPromoSection />

      {/* About Section */}
      <section className="py-20 md:py-32" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-barlow uppercase tracking-widest text-primary text-sm font-bold mb-4">About WayBetter</p>
              <h2 className="font-barlow font-bold text-4xl md:text-5xl text-stone-900 uppercase mb-6 leading-tight">
                You Grow It, We Mow It — We Want Your Green!
              </h2>
              <div className="section-divider mb-8"></div>
              <p className="font-manrope text-stone-600 text-lg leading-relaxed mb-6">
                Based in Menomonee Falls, WI, WayBetter Landscaping & Snowplowing has been transforming properties across Metro Milwaukee since 2014. We're a trusted local company with dedicated crews who take pride in every property we service.
              </p>
              <p className="font-manrope text-stone-600 text-lg leading-relaxed mb-8">
                With a strong commitment to excellence, we deliver landscaping and snow removal services that exceed expectations. Our team combines smart planning, well-maintained equipment, and trained crews to keep your grounds clean, safe, and inviting year-round.
              </p>

              {/* Values Grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {['Commitment', 'Responsibility', 'Honesty', 'Excellence'].map((value) => (
                  <div key={value} className="trust-badge p-4">
                    <span className="font-barlow font-semibold uppercase tracking-wide text-stone-800">{value}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-primary text-white hover:bg-primary-600 shadow-truck active:translate-y-[2px] active:shadow-none transition-all font-barlow font-bold uppercase tracking-wider py-4 px-8"
                data-testid="about-learn-more-btn"
              >
                Learn More About Us
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="relative">
              <img 
                src="/images/commercial-1.webp"
                alt="WayBetter team at work"
                className="rounded-lg shadow-2xl"
                width="600"
                height="450"
                loading="lazy"
                decoding="async"
                onError={handleImageError}
              />
              <div className="absolute -bottom-6 -left-6 bg-primary text-white p-6 rounded-lg shadow-xl">
                <p className="font-barlow font-bold text-4xl">10+</p>
                <p className="font-manrope text-sm text-primary-100">Years of Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-32 bg-stone-900 text-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-barlow uppercase tracking-widest text-accent text-sm font-bold mb-4">Testimonials</p>
            <h2 className="font-barlow font-bold text-4xl md:text-5xl uppercase mb-4">
              What Our Clients Say
            </h2>
            <div className="section-divider mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-stone-800 p-8 rounded-lg relative"
                data-testid={`testimonial-${index}`}
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-accent fill-accent" />
                  ))}
                </div>
                <blockquote className="font-manrope text-stone-300 text-lg leading-relaxed mb-6">
                  "{testimonial.quote}"
                </blockquote>
                <div>
                  <p className="font-barlow font-semibold uppercase tracking-wide text-white">{testimonial.author}</p>
                  <p className="font-manrope text-stone-500 text-sm">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas with Internal Links */}
      <section className="py-20 md:py-32" data-testid="service-areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-barlow uppercase tracking-widest text-primary text-sm font-bold mb-4">Coverage Area</p>
            <h2 className="font-barlow font-bold text-4xl md:text-5xl text-stone-900 uppercase mb-4">
              Proudly Serving Metro Milwaukee
            </h2>
            <div className="section-divider mx-auto"></div>
          </div>

          {/* City Page Links - SEO Internal Linking */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {cityPages.map((city) => (
              <Link 
                key={city.slug}
                to={city.path}
                className="flex items-center justify-center gap-2 bg-stone-100 hover:bg-primary hover:text-white px-4 py-4 rounded-lg transition-colors group"
              >
                <MapPin className="w-4 h-4 text-primary group-hover:text-white" />
                <span className="font-barlow font-semibold uppercase tracking-wide text-stone-700 group-hover:text-white text-sm">
                  {city.name}, WI
                </span>
              </Link>
            ))}
          </div>

          <p className="text-center font-manrope text-stone-500 mb-12">
            ...and surrounding Metro Milwaukee communities
          </p>

          {/* Map Placeholder or Additional Info */}
          <div className="bg-stone-100 p-8 rounded-lg text-center">
            <Truck className="w-12 h-12 text-primary mx-auto mb-4" />
            <h3 className="font-barlow font-bold text-2xl uppercase text-stone-900 mb-2">
              Local Crew, Quick Response
            </h3>
            <p className="font-manrope text-stone-600 max-w-2xl mx-auto">
              Our crews know the roads, the weather patterns, and the trouble spots in Metro Milwaukee. You're not dealing with a giant company — you get a consistent team that treats your property like their own.
            </p>
          </div>

          {/* Snow Removal Footer Note (only in landscaping season) */}
          {seasonCfg.snowMention && (
            <div className="mt-12 text-center border-t border-stone-200 pt-8">
              <p className="font-manrope text-stone-600">
                <Snowflake className="w-4 h-4 inline mr-2 text-primary" />
                Looking for winter services? We also offer{' '}
                <Link to="/snow-removal" className="text-primary font-semibold hover:underline">
                  24/7 commercial and residential snow removal
                </Link>{' '}
                throughout Metro Milwaukee.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Google Reviews */}
      <GoogleReviews 
        title="What Our Customers Say"
        subtitle="Real reviews from Metro Milwaukee property owners"
        maxReviews={4}
      />

      {/* Lawn Care Tips - Internal Links to Blog */}
      <section className="py-16 bg-stone-50" data-testid="blog-links-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="font-barlow uppercase tracking-widest text-primary text-sm font-bold mb-2">Lawn Care Tips</p>
            <h2 className="font-barlow font-bold text-3xl text-stone-900 uppercase">
              Expert Advice from Our Blog
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {blogPostsForLinking.map((post) => (
              <Link
                key={post.path}
                to={post.path}
                className="bg-white hover:bg-primary hover:text-white p-4 rounded-lg shadow-sm border border-stone-200 hover:border-primary transition-colors text-center group"
              >
                <span className="font-manrope text-sm text-stone-700 group-hover:text-white">
                  {post.title}
                </span>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-primary font-barlow font-semibold uppercase tracking-wider text-sm hover:gap-3 transition-all"
            >
              View All Articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-primary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-barlow font-bold text-4xl md:text-5xl text-white uppercase mb-6">
            Ready to Make Your Property Look Way Better?
          </h2>
          <p className="font-manrope text-xl text-primary-100 mb-8">
            Get $75 off your first package with a free, no-obligation property assessment — limited availability.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-primary hover:bg-stone-100 shadow-truck active:translate-y-[2px] active:shadow-none transition-all font-barlow font-bold uppercase tracking-wider py-4 px-8 text-lg"
              data-testid="cta-quote-btn"
            >
              Claim Your Free Assessment
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="tel:414-795-3610"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-white border-2 border-white hover:bg-white/10 font-barlow font-bold uppercase tracking-wider py-4 px-8 text-lg"
              data-testid="cta-call-btn"
            >
              <Phone className="w-5 h-5" />
              (414) 795-3610
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
